import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Plane, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/Footer';
import DuplicateBookingPanel from '../components/DuplicateBookingPanel';
import { useAuth } from '../context/AuthContext';
import { useAgentAuth } from '../context/AgentAuthContext';
import { trackBookConfirmed } from '../utils/analytics';
import { getApiBookingRouteId, getUserFacingBookingId } from '../utils/userFacingBookingId';
import {
  getLegBaggageRowsForBooking,
  getNonBaggageSsrRowsForBooking,
} from '../utils/ssrBreakdown';
import { tripjackSessionBookingId } from '../utils/tripjackSessionBookingId';
import { bookingSessionHeaders } from '../utils/bookingSessionCorrelation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const round2 = (n) => Math.round(Number(n) * 100) / 100;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated: isB2CAuth } = useAuth();
  const { agent, isAuthenticated: isAgentAuth } = useAgentAuth();
  
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  /** Tripjack 2502 after gateway payment — booking already exists at supplier. */
  const [duplicateBooking, setDuplicateBooking] = useState(null);

  // Idempotency guard — React 18 StrictMode fires useEffect twice in dev,
  // and hot-reloads can also re-mount this page. Without this guard, two
  // POST /api/tripjack/book calls fire ~100ms apart, the supplier accepts
  // the second one (real PNR ticketed), but the first one races to error
  // with code 2714 / "duplicate booking" and overwrites the UI state.
  // Booking call must fire EXACTLY ONCE per session token, ever.
  const bookingFiredRef = useRef(false);

  useEffect(() => {
    if (bookingFiredRef.current) return;
    bookingFiredRef.current = true;
    createInstantBooking();
  }, []);
  
  const createInstantBooking = async () => {
    try {
      // Get payment details from URL
      const orderId = searchParams.get('order_id');
      const trackingId = searchParams.get('tracking_id');
      const paymentStatus = searchParams.get('status');
      const verified = searchParams.get('verified');

      if (verified !== '1' || (paymentStatus || '').toLowerCase() !== 'success') {
        setError('Payment was not verified. Please contact support if money was deducted.');
        setProcessing(false);
        return;
      }
      
      console.log('💳 Payment Success Callback:', { orderId, trackingId, paymentStatus, verified });
      
      // Sprint-4 (Feb 2026) · DPDP-compliance: read booking payload from
      // server-side payment session via sessionStorage token. Fall back
      // to legacy localStorage entry only when no token is present (so
      // bookings created right around the deploy boundary still work).
      let bookingData = null;
      const sessionToken = sessionStorage.getItem('paymentSessionToken');
      if (sessionToken) {
        try {
          const sessResp = await axios.get(
            `${BACKEND_URL}/api/payment/session/${encodeURIComponent(sessionToken)}`
          );
          bookingData = sessResp.data?.payload || null;
          console.log('🔒 Retrieved booking payload from server-side session');
        } catch (sessErr) {
          console.warn('⚠️ Payment session lookup failed (will try legacy fallback):', sessErr?.response?.status);
        }
      }
      if (!bookingData) {
        const pendingBookingData = localStorage.getItem('pendingBooking');
        if (!pendingBookingData) {
          // Distinguish "session missing" (likely page refresh / direct
          // visit / already-consumed token) from "payment captured but
          // booking call failed". The first case must NOT alarm the user
          // with "payment succeeded but booking failed" — there's no
          // evidence of a captured payment in this flow.
          setError('__NO_PENDING_SESSION__');
          setProcessing(false);
          return;
        }
        bookingData = JSON.parse(pendingBookingData);
        console.log('📦 Retrieved booking data from localStorage (legacy path)');
      }
      
      // ── Source-based dispatch ──────────────────────────────────────────
      // The same post-payment flow handles 3 suppliers. Offline is handled
      // via `offlineBookingPending` (pre-created before payment). Riya and
      // Tripjack need full book API calls here.
      const src = (bookingData?.flightData?.source || '').toLowerCase();
      console.log(`✈️  Source-aware post-payment booking: source='${src || 'tripjack'}'`);

      // Determine user type and ID (shared across suppliers)
      let userId = null;
      let userType = 'guest';
      let paymentMethod = 'gateway';

      if (isAgentAuth && agent) {
        userId = agent.id;
        userType = 'agent';
        const walletPayment = localStorage.getItem('agentWalletPayment');
        if (walletPayment === 'true') paymentMethod = 'wallet';
      } else if (isB2CAuth && user) {
        userId = user.id;
        userType = 'b2c';
      }

      let bookingResponse;

      if (src === 'riya') {
        // Riya B2C booking — runs Pricing → Book → IssueTicket server-side
        console.log('🟢 Creating Riya booking via /api/riya/book-b2c');
        bookingResponse = await axios.post(
          `${BACKEND_URL}/api/riya/book-b2c`,
          {
            flightData: bookingData.flightData,
            returnFlightData: bookingData.flightData?.return || null,
            passengers: bookingData.passengers,
            contact_email: bookingData.contactEmail,
            contact_phone: bookingData.contactPhone,
            total_amount: bookingData.totalAmount,
            order_id: orderId,
            tracking_id: trackingId,
            user_id: userId,
            user_type: userType,
            gst_enabled: !!bookingData.gstEnabled,
            gst_details: bookingData.gstDetails || null,
            ssrSelections: bookingData.ssrSelections || null,
            // Sprint-2 · Riya RT return-leg SSR. Backend already accepts
            // `returnSsrSelections` (riya_booking.py:706) — FE was not
            // forwarding it, so return-leg ancillaries silently dropped.
            returnSsrSelections: bookingData.returnSsrSelections || null,
          },
          { headers: { ...bookingSessionHeaders() } },
        );
      } else {
        // Tripjack (default) post-payment booking
        console.log('✈️ Creating Tripjack instant booking via /api/tripjack/book');
        const _tjReviewSnap = bookingData.reviewData || null;
        const _tjBid = tripjackSessionBookingId(
          _tjReviewSnap,
          bookingData.tripjackBookingId,
        );
        const instantBookingPayload = {
          priceIds: bookingData.priceIds,
          travellerInfo: bookingData.passengers,
          ...(_tjReviewSnap && _tjBid
            ? { reviewSnapshot: _tjReviewSnap, tripjackBookingId: _tjBid }
            : {}),
          deliveryInfo: {
            emails: [bookingData.contactEmail],
            contacts: [bookingData.contactPhone],
          },
          paymentInfos: [{
            amount: bookingData.totalAmount,
            flight_data: bookingData.flightData,
          }],
          gateway_order_id: orderId,
          gateway_tracking_id: trackingId,
          gstInfo: bookingData.gstEnabled && bookingData.gstDetails ? {
            gstNumber: bookingData.gstDetails.gstNumber,
            registeredName: bookingData.gstDetails.registeredName,
            email: bookingData.gstDetails.email,
            mobile: bookingData.gstDetails.mobile,
            address: bookingData.gstDetails.address,
          } : null,
          // G3 · Emergency contact (only present when fare's iecr=true was
          // detected on /review). Backend validates + forwards as Tripjack
          // /book contactInfo block.
          contactInfo: bookingData.emergencyContact ? {
            ecn:      bookingData.emergencyContact.name,
            emails:   [bookingData.emergencyContact.email].filter(Boolean),
            contacts: [bookingData.emergencyContact.phone].filter(Boolean),
          } : null,
        };
        if (bookingData.addOnsIds && Object.keys(bookingData.addOnsIds).length > 0) {
          instantBookingPayload.addOnsIds = bookingData.addOnsIds;
        }
        const params = {};
        if (userId) params.user_id = userId;
        if (userType) params.user_type = userType;
        if (paymentMethod) params.payment_method = paymentMethod;
        bookingResponse = await axios.post(
          `${BACKEND_URL}/api/tripjack/book`,
          instantBookingPayload,
          { params, headers: { ...bookingSessionHeaders() } },
        );
      }
      
      if (bookingResponse.data.success) {
        console.log('✅ Instant booking created successfully!');
        console.log('Booking ID:', bookingResponse.data.bookingId);
        
        // Normalise the supplier response shapes — Tripjack uses `bookingId`/`pnr`,
        // Riya's /book-b2c returns `booking_id`/`pnr`/`airline_pnr`.
        const respData = bookingResponse.data;
        const idEntity = {
          bookingId: respData.bookingId,
          booking_id: respData.booking_id,
          acf_booking_id: respData.acf_booking_id,
          id: respData.id,
        };
        const normalisedBookingId =
          getApiBookingRouteId(idEntity)
          || (() => {
            const u = getUserFacingBookingId(idEntity);
            return u !== '—' ? u : (respData.booking_id || respData.bookingId || orderId);
          })();
        const normalisedPnr = respData.pnr || respData.airline_pnr || null;

        setBookingDetails({
          bookingId: normalisedBookingId,
          orderId: orderId,
          trackingId: trackingId,
          amount: bookingData.totalAmount,
          passengers: bookingData.passengersRaw || bookingData.passengers,
          flightData: bookingData.flightData,
          contactEmail: bookingData.contactEmail,
          contactPhone: bookingData.contactPhone,
          ssrSelections: bookingData.ssrSelections,
          gstEnabled: bookingData.gstEnabled,
          source: src || 'tripjack',
          pnr: normalisedPnr,
          seat_warning: respData.seat_warning || null,
          // Audit §5.1 — surface PENDING_SUPPLIER_CONFIRMATION state to UI
          booking_status: respData.booking_status || null,
          statusMeta: respData.statusMeta || null,
          ticket_status: respData.ticket_status || null,
        });

        // Analytics: terminal funnel event — fires immediately (not buffered)
        trackBookConfirmed({
          booking_id: normalisedBookingId,
          source: src || 'tripjack',
          total_amount: bookingData.totalAmount,
          route: `${bookingData.flightData?.from || ''} → ${bookingData.flightData?.to || ''}`,
          pnr: normalisedPnr,
        });
        
        // Legacy B2C GST PDF — disabled server-side unless LEGACY_B2C_INVOICE_ENABLED=true
        const legacyB2cInvoice =
          process.env.REACT_APP_LEGACY_B2C_INVOICE_ENABLED === 'true';
        if (legacyB2cInvoice && bookingData.gstEnabled && bookingData.gstDetails) {
          try {
            console.log('📄 Generating GST invoice...');
            const invoiceResponse = await axios.post(`${BACKEND_URL}/api/invoices/generate`, {
              booking_id: normalisedBookingId,
              pnr: normalisedPnr || 'PENDING',
              flight_details: {
                origin: bookingData.flightData.from || bookingData.flightData.route?.origin || 'N/A',
                destination: bookingData.flightData.to || bookingData.flightData.route?.destination || 'N/A',
                airline: bookingData.flightData.airlineName || bookingData.flightData.flight?.airlineName || 'N/A',
                flight_number: bookingData.flightData.flightNumber || bookingData.flightData.flight?.flightNumber || 'N/A',
                departure_time: bookingData.flightData.departureTime || bookingData.flightData.flight?.departureTime || 'N/A',
                arrival_time: bookingData.flightData.arrivalTime || bookingData.flightData.flight?.arrivalTime || 'N/A'
              },
              passengers: (bookingData.passengersRaw || bookingData.passengers).map(p => ({
                title: p.ti || p.title || 'Mr',
                first_name: p.fN || p.firstName || '',
                last_name: p.lN || p.lastName || '',
                type: p.pt || p.type || 'ADULT',
                age: p.age || null
              })),
              pricing: {
                // Feb-2026 · Use actual supplier/markup split from the /book
                // response when available; fall back to a conservative heuristic
                // only if the backend didn't surface them (legacy records).
                supplier_price: (
                  typeof bookingResponse.data.supplier_total === 'number'
                    ? bookingResponse.data.supplier_total
                    : bookingData.totalAmount * 0.9
                ),
                markup: (
                  typeof bookingResponse.data.markup === 'number'
                    ? bookingResponse.data.markup
                    : bookingData.totalAmount * 0.1
                ),
                gst_on_markup: round2(
                  (typeof bookingResponse.data.markup === 'number'
                    ? bookingResponse.data.markup
                    : bookingData.totalAmount * 0.1) * 0.18
                ),
                total: bookingData.totalAmount
              },
              contact_email: bookingData.contactEmail,
              contact_phone: bookingData.contactPhone,
              customer_gst: bookingData.gstDetails
            });
            
            if (invoiceResponse.data.success) {
              console.log('✅ Invoice generated:', invoiceResponse.data.invoice_number);
              setBookingDetails(prev => ({
                ...prev,
                invoiceNumber: invoiceResponse.data.invoice_number,
                invoiceGenerated: true
              }));
            }
          } catch (invoiceError) {
            console.error('❌ Invoice generation failed:', invoiceError);
            // Don't fail the booking if invoice fails
          }
        }
        
        // Clear pending booking and wallet payment flag
        // Sprint-4 · purge BOTH the legacy localStorage key (back-compat)
        // AND the server-side payment session + sessionStorage token.
        localStorage.removeItem('pendingBooking');
        localStorage.removeItem('holdBooking');
        localStorage.removeItem('selectedFlight');
        localStorage.removeItem('agentWalletPayment');
        try {
          const tok = sessionStorage.getItem('paymentSessionToken');
          if (tok) {
            sessionStorage.removeItem('paymentSessionToken');
            // Fire-and-forget — TTL will sweep it eventually anyway.
            axios.delete(`${BACKEND_URL}/api/payment/session/${encodeURIComponent(tok)}`).catch(() => {});
          }
        } catch { /* ignore */ }
        
        setProcessing(false);
      } else {
        console.error('❌ Booking creation failed:', bookingResponse.data);
        setError(bookingResponse.data.message || 'Failed to create booking with Tripjack');
        setProcessing(false);
      }
    } catch (err) {
      console.error('💥 Instant booking error:', err);
      
      let errorMessage = 'Failed to create booking';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail.code === 'PAX_FIELDS_MISSING' && Array.isArray(detail.errors)) {
          // Backend's authoritative validation against Tripjack /review form_conditions.
          // Render the per-pax missing-field list so support can help the user correct it.
          const lines = detail.errors.map(e => `• ${e.message || `${e.pax_type} pax #${(e.pax_index ?? 0) + 1}: ${e.field}`}`);
          errorMessage = `${detail.message || 'This fare requires additional passenger information.'}\n\n${lines.join('\n')}`;
        } else if (detail.action === 'lookup_existing' || Number(detail.code) === 2502) {
          setDuplicateBooking({
            message: detail.message || undefined,
            acfBookingId: detail.existing_booking_id || detail.acf_booking_id,
          });
          setProcessing(false);
          return;
        } else if (detail.code === 'FARE_CHANGED') {
          // B4 · fare moved between search and book. Payment may have been
          // captured already — user needs to contact support for refund or
          // re-search.
          errorMessage =
            `${detail.message || 'The fare has changed since you started booking.'}\n\n` +
            `Old fare: ₹${Number(detail.old_fare || 0).toLocaleString('en-IN')}\n` +
            `New fare: ₹${Number(detail.new_fare || 0).toLocaleString('en-IN')}\n\n` +
            `Your payment was successful but the booking could not be created at the new price. ` +
            `Please contact support with your order ID for a refund.`;
        } else if (detail.message) {
          errorMessage = detail.message;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setProcessing(false);
    }
  };
  
  if (duplicateBooking) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-xl mb-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Payment received</h1>
          <p className="mt-2 text-sm text-gray-600">
            We could not create a second booking for the same passengers and itinerary. Use your ACF booking below, or contact support if you think you were charged incorrectly.
          </p>
        </div>
        <DuplicateBookingPanel
          message={duplicateBooking.message}
          acfBookingId={duplicateBooking.acfBookingId}
          isAgent={!!isAgentAuth}
          onViewBooking={
            duplicateBooking.acfBookingId
              ? () => navigate(`/booking-details/${encodeURIComponent(duplicateBooking.acfBookingId)}`)
              : undefined
          }
          onGoBookings={() => navigate(isAgentAuth ? '/agent/bookings' : '/user/dashboard/bookings')}
          onGoHome={() => navigate('/')}
        />
        <Footer />
      </div>
    );
  }

  if (processing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md w-full text-center">
          <Loader className="w-16 h-16 text-blue-600 mx-auto mb-6 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Processing Your Booking</h2>
          <p className="text-gray-600">
            Payment successful! Creating your flight booking...
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Please do not close this window
          </p>
        </div>
      </div>
    );
  }
  
  if (error) {
    // Differentiate "no session" (likely refresh / direct visit) from a real
    // payment-vs-booking mismatch so we don't terrify the user unnecessarily.
    const isNoSession = error === '__NO_PENDING_SESSION__';
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="payment-success-error-card">
            {isNoSession ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                  <Plane className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">No active booking session</h2>
                <p className="text-gray-600 mb-6">
                  Looks like this page was refreshed or opened directly. If you've
                  already booked, you can find it in <span className="font-semibold">My Bookings</span>.
                </p>
                <div className="space-y-3">
                  {(isB2CAuth || isAgentAuth) && (
                    <button
                      onClick={() => navigate(isAgentAuth ? '/agent/bookings' : '/user/dashboard/bookings')}
                      className="w-full px-6 py-3 bg-[#1B375B] text-white rounded-lg font-bold hover:bg-[#335E77] transition-colors"
                      data-testid="goto-my-bookings-btn"
                    >
                      View My Bookings
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/')}
                    className="w-full px-6 py-3 bg-white text-[#1B375B] border-2 border-[#1B375B] rounded-lg font-bold hover:bg-gray-50 transition-colors"
                    data-testid="back-to-home-btn"
                  >
                    Start a New Search
                  </button>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Booking Failed</h2>
                <p className="text-gray-600 mb-6 whitespace-pre-line text-left" data-testid="payment-success-error">{error}</p>
                <div className="space-y-3">
                  <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    Your payment was successful, but we couldn&apos;t create the booking. Please contact support with your order ID.
                  </p>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full px-6 py-3 bg-[#1B375B] text-white rounded-lg font-bold hover:bg-[#335E77] transition-colors"
                  >
                    Back to Home
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (bookingDetails) {
    // Audit §5.1 — Tripjack /book may return order.status=PENDING (HTTP 200).
    // Backend stamps booking_status=PENDING_SUPPLIER_CONFIRMATION and surfaces
    // statusMeta. B2C UI must reflect that — NOT show hardcoded "CONFIRMED".
    const isPendingSupplier =
      bookingDetails?.booking_status === 'PENDING_SUPPLIER_CONFIRMATION'
      || bookingDetails?.statusMeta?.label === 'Pending Supplier Confirmation'
      || (bookingDetails?.ticket_status === 'PENDING' && !bookingDetails?.pnr);

    const headerGradient = isPendingSupplier
      ? 'from-amber-500 to-orange-500'
      : 'from-green-500 to-teal-500';
    const headerIconColor = isPendingSupplier ? 'text-amber-600' : 'text-green-600';
    const headerSubtextColor = isPendingSupplier ? 'text-amber-50' : 'text-green-100';

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Success Header */}
            <div className={`bg-gradient-to-r ${headerGradient} px-8 py-10 text-center`} data-testid="payment-success-header">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                {isPendingSupplier ? (
                  <AlertCircle className={`w-14 h-14 ${headerIconColor}`} />
                ) : (
                  <Check className={`w-14 h-14 ${headerIconColor}`} />
                )}
              </div>
              <h1 className="text-4xl font-bold text-white mb-3" data-testid="payment-success-headline">
                {isPendingSupplier ? 'Booking Submitted' : 'Booking Confirmed!'}
              </h1>
              <p className={`${headerSubtextColor} text-lg`} data-testid="payment-success-subtext">
                {isPendingSupplier
                  ? 'Supplier confirmation pending — PNR/ticket will appear once the airline confirms.'
                  : 'Your flight has been booked successfully'}
              </p>
            </div>
            
            {/* Booking Details */}
            <div className="p-8">
              {bookingDetails.seat_warning && (
                <div
                  className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  role="status"
                  data-testid="payment-success-seat-warning"
                >
                  <strong className="font-semibold">Seat note:</strong>{' '}
                  {bookingDetails.seat_warning}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                  <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                    <Plane className="w-5 h-5 text-blue-600" />
                    Booking Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Booking ID</p>
                      <p className="font-mono font-bold text-sm text-blue-600">{getUserFacingBookingId({ ...bookingDetails, bookingId: bookingDetails.bookingId, booking_id: bookingDetails.booking_id })}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Order ID</p>
                      <p className="font-mono font-bold text-sm text-gray-800">{bookingDetails.orderId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Payment Tracking ID</p>
                      <p className="font-mono text-sm text-gray-600">{bookingDetails.trackingId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Status</p>
                      <p
                        className={`font-semibold flex items-center gap-1 ${isPendingSupplier ? 'text-amber-600' : 'text-green-600'}`}
                        data-testid="payment-success-status-badge"
                      >
                        <span className={`w-2 h-2 rounded-full ${isPendingSupplier ? 'bg-amber-500' : 'bg-green-500'}`}></span>
                        {isPendingSupplier ? 'PENDING' : 'CONFIRMED'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                  <h3 className="font-bold text-lg text-gray-800 mb-4">Passenger Details</h3>
                  <div className="space-y-3">
                    {bookingDetails.passengers.map((p, idx) => (
                      <div key={idx}>
                        <p className="text-xs text-gray-500 uppercase">{p.type || p.pt} {idx + 1}</p>
                        <p className="font-semibold text-gray-800">
                          {p.title || p.ti} {p.firstName || p.fN} {p.lastName || p.lN}
                        </p>
                      </div>
                    ))}
                    <div className="pt-2 border-t">
                      <p className="text-xs text-gray-500 uppercase">Contact</p>
                      <p className="text-sm text-gray-600">{bookingDetails.contactEmail}</p>
                      <p className="text-sm text-gray-600">{bookingDetails.contactPhone}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* SSR Summary — leg-grouped baggage + per-segment meals/seats.
                   Industry standard: a 4-segment RT trip with the same 15 KG
                   bag attached to every segment shows ONE row per leg
                   (BOM → DXB · ₹3,000) instead of 4 confusing per-segment lines. */}
              {(() => {
                const legBag = getLegBaggageRowsForBooking(bookingDetails);
                const nonBag = getNonBaggageSsrRowsForBooking(bookingDetails);
                const grouped =
                  bookingDetails.ssrSelections || bookingDetails.ssr_selections || null;

                const groupedHas =
                  grouped && (
                    (grouped.baggage && Object.keys(grouped.baggage).length > 0)
                    || (grouped.meals && Object.keys(grouped.meals).length > 0)
                    || (grouped.seats && Object.keys(grouped.seats).length > 0)
                    || (grouped.other && Object.keys(grouped.other).length > 0)
                  );
                const hasFlat = legBag.length > 0 || nonBag.length > 0;
                if (!hasFlat && !groupedHas) return null;

                // Flat list path (Tripjack) — itemised rows with prices.
                if (hasFlat) {
                  return (
                    <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 mb-6"
                         data-testid="addons-panel">
                      <h3 className="font-bold text-lg text-gray-800 mb-3">Selected Add-ons</h3>
                      <ul className="divide-y divide-purple-100">
                        {legBag.map((row, i) => (
                          <li key={`bag-${i}`} className="flex items-center justify-between py-2 text-sm" data-testid={`success-addon-bag-${i}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-base" aria-hidden="true">🧳</span>
                              <div>
                                <p className="text-gray-800 font-medium">
                                  {row.description || row.code}
                                  {row.segment_count > 1 ? (
                                    <span className="ml-2 text-[10px] font-semibold text-gray-500">
                                      · spans {row.segment_count} segs
                                    </span>
                                  ) : null}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {row.pax_name || `Passenger ${row.pax_ref || row.pax_index}`}
                                  {' · '}{row.leg_label === 'return' ? 'Return' : 'Onward'}
                                  {row.leg_route ? ` · ${row.leg_route}` : ''}
                                  {' · Code '}{row.code}
                                </p>
                              </div>
                            </div>
                            <span className="font-semibold text-gray-700">
                              ₹{Number(row.total_amount || 0).toLocaleString()}
                            </span>
                          </li>
                        ))}
                        {nonBag.map((row, i) => {
                          const symbols = { meals: '🍽️', seats: '💺', other: '✨' };
                          return (
                            <li key={`nb-${i}`} className="flex items-center justify-between py-2 text-sm" data-testid={`success-addon-nb-${i}`}>
                              <div className="flex items-center gap-2">
                                <span className="text-base" aria-hidden="true">
                                  {symbols[row.kind] || '✨'}
                                </span>
                                <div>
                                  <p className="text-gray-800 font-medium">{row.description}</p>
                                  <p className="text-xs text-gray-500">
                                    {row.pax_name || `Passenger ${row.pax_index}`}
                                    {row.leg === 'return' ? ' · Return' : ''}
                                    {' · Code '}{row.code}
                                  </p>
                                </div>
                              </div>
                              <span className="font-semibold text-gray-700">
                                ₹{Number(row.amount || 0).toLocaleString()}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                }

                // Grouped (Riya legacy) — keep the existing summary card.
                return (
                  <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 mb-6">
                    <h3 className="font-bold text-lg text-gray-800 mb-3">Selected Add-ons</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {grouped?.baggage && Object.keys(grouped.baggage).length > 0 && (
                        <div>
                          <p className="text-xs text-purple-600 font-semibold uppercase mb-1">Extra Baggage</p>
                          <p className="text-sm text-gray-700">
                            {Object.keys(grouped.baggage).length} passenger(s)
                          </p>
                        </div>
                      )}
                      {grouped?.meals && Object.keys(grouped.meals).length > 0 && (
                        <div>
                          <p className="text-xs text-purple-600 font-semibold uppercase mb-1">Meals</p>
                          <p className="text-sm text-gray-700">
                            {Object.keys(grouped.meals).length} passenger(s)
                          </p>
                        </div>
                      )}
                      {grouped?.seats && Object.keys(grouped.seats).length > 0 && (
                        <div>
                          <p className="text-xs text-purple-600 font-semibold uppercase mb-1">Seats</p>
                          <p className="text-sm text-gray-700">
                            {Object.keys(grouped.seats).length} passenger(s)
                          </p>
                        </div>
                      )}
                    </div>
                    {groupedReturn && (
                      (groupedReturn.baggage && Object.keys(groupedReturn.baggage).length > 0)
                      || (groupedReturn.meals && Object.keys(groupedReturn.meals).length > 0)
                      || (groupedReturn.seats && Object.keys(groupedReturn.seats).length > 0)
                    ) && (
                      <p className="mt-3 text-xs text-purple-700">+ return-leg add-ons attached</p>
                    )}
                  </div>
                );
              })()}
              
              {/* Amount Paid */}
              <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] rounded-2xl p-6 mb-6 text-white">
                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-90">Total Amount Paid</span>
                  <span className="text-3xl font-bold">₹{bookingDetails.amount.toLocaleString()}</span>
                </div>
              </div>
              
              {/* Important Notice */}
              <div className={`${isPendingSupplier ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'} border rounded-2xl p-6 mb-6`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-6 h-6 ${isPendingSupplier ? 'text-amber-600' : 'text-blue-600'} flex-shrink-0 mt-1`} />
                  <div>
                    <h4 className={`font-bold ${isPendingSupplier ? 'text-amber-900' : 'text-blue-900'} mb-2`}>
                      {isPendingSupplier ? 'Awaiting Supplier Confirmation' : 'Booking Confirmed'}
                    </h4>
                    <p className={`text-sm ${isPendingSupplier ? 'text-amber-800' : 'text-blue-800'} leading-relaxed`} data-testid="payment-success-notice">
                      {isPendingSupplier ? (
                        <>
                          Your payment was successful and the booking has been submitted. The supplier (airline) is still confirming the seat — the final PNR and e-ticket will be issued shortly.
                          A confirmation email will be sent to <strong>{bookingDetails.contactEmail}</strong> as soon as the ticket is issued. You can also track the status under <strong>My Bookings</strong>.
                        </>
                      ) : (
                        <>
                          Your booking has been confirmed and ticket has been issued.
                          Booking confirmation and e-ticket have been sent to <strong>{bookingDetails.contactEmail}</strong>.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* GST Invoice Download */}
              {bookingDetails.gstEnabled && bookingDetails.invoiceGenerated && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-green-900 mb-1">GST Invoice Generated</h4>
                      <p className="text-sm text-green-700">
                        Invoice Number: <span className="font-mono font-bold">{bookingDetails.invoiceNumber}</span>
                      </p>
                    </div>
                    <a
                      href={`${BACKEND_URL}/api/invoices/download/${bookingDetails.bookingId}`}
                      download
                      className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-md flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Invoice
                    </a>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Book Another Flight
                </button>
                <button
                  onClick={() => navigate('/bookings')}
                  className="flex-1 px-6 py-4 bg-[#1B375B] text-white rounded-xl font-semibold hover:bg-[#335E77] transition-colors shadow-lg"
                >
                  View My Bookings
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return null;
};

export default PaymentSuccess;
